import { useQuery } from "@tanstack/react-query";
import { getSupabase } from "@/lib/hooks/api/utils";
import { buildIlikeOrFilter } from "@west-tennessee-consulting/supabase/postgrest-filter";

export function useContactProposals(params?: {
  page?: number;
  limit?: number;
  search?: string;
  contact_id?: string;
  status?: string;
}) {
  return useQuery({
    queryKey: ["contact-proposals", params],
    queryFn: async () => {
      const supabase = getSupabase();
      let query = supabase.from("contact_proposals").select(`
                    *,
                    contacts:contact_id (
                        id,
                        name,
                        company_name,
                        email
                    ),
                    users:app_users(
                        id,
                        first_name,
                        last_name,
                        email
                    )
                `);

      if (params?.contact_id) {
        query = query.eq("contact_id", params.contact_id);
      }
      if (params?.status) {
        query = query.eq("status", params.status);
      }
      if (params?.search) {
        // User text is sanitized: commas/parens would otherwise rewrite the PostgREST filter.
        const searchFilter = buildIlikeOrFilter(["title", "description"], params?.search);
        if (searchFilter) query = query.or(searchFilter);
      }

      if (params?.limit) {
        const offset = (params.page || 0) * params.limit;
        query = query.range(offset, offset + params.limit - 1);
      }

      const { data, error } = await query.order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching contact proposals:", error);
        throw new Error(error.message);
      }

      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useContactProposal(id: string) {
  return useQuery({
    queryKey: ["contact-proposals", id],
    queryFn: async () => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("contact_proposals")
        .select(`
                    *,
                    contacts:contact_id (
                        id,
                        name,
                        company_name,
                        email
                    ),
                    users:app_users(
                        id,
                        first_name,
                        last_name,
                        email
                    )
                `)
        .eq("id", id)
        .single();

      if (error) {
        console.error("Error fetching contact proposal:", error);
        throw new Error(error.message);
      }

      return data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// Backward compatibility alias
export function useContractProposals(params?: {
  contact_id?: string;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}) {
  return useContactProposals(params);
}
